import { useEffect } from 'react';
import { useLatestRef } from '@/hooks/useLatestRef';
import { retryChargebeePaymentIntent } from '@/services/chargebeeService';
import { CHARGEBEE_INTENT_RETRIEVAL_ATTEMPTS, CHARGEBEE_INTENT_RETRIEVAL_RETRY_DELAY_MS } from '@/constants/chargebee';
import { logError } from '@/utils/logger';
import type { ChargebeeInstance, ChargebeePaymentIntent } from '@/types/chargebee';

interface UseChargebeePaymentIntentParams {
  cbInstance?: ChargebeeInstance | null;
  paymentIntentId?: string;
  /**
   * A ZIP tax recalc rewrites the intent's amount SERVER-side under the same
   * id, so the local copy has to be pulled again whenever the total moves.
   */
  priceTotal?: number;
  onHydrated: (paymentIntent: ChargebeePaymentIntent) => void;
  /**
   * Every retry failed. Card can still recover at pay time (it retrieves again
   * before authorizing), but the wallet buttons have nothing to mount with and
   * must collapse instead of spinning forever.
   */
  onUnavailable: () => void;
}

/**
 * Replaces the `{ id }`-only payment intent from `/checkout/subscriptions`
 * with the full Chargebee object every Fields-mode entry point validates
 * against (see `chargebeeService.retrievePaymentIntent`).
 */
export const useChargebeePaymentIntent = ({
  cbInstance,
  paymentIntentId,
  priceTotal,
  onHydrated,
  onUnavailable,
}: UseChargebeePaymentIntentParams): void => {
  const onHydratedRef = useLatestRef(onHydrated);
  const onUnavailableRef = useLatestRef(onUnavailable);

  useEffect(() => {
    if (!cbInstance || !paymentIntentId) return;
    let cancelled = false;

    retryChargebeePaymentIntent(
      paymentIntentId,
      CHARGEBEE_INTENT_RETRIEVAL_ATTEMPTS,
      CHARGEBEE_INTENT_RETRIEVAL_RETRY_DELAY_MS
    )
      .then((paymentIntent) => {
        if (!cancelled) onHydratedRef.current(paymentIntent);
      })
      .catch((error: unknown) => {
        logError('Chargebee payment intent retrieval failed', error, { paymentIntentId });
        if (!cancelled) onUnavailableRef.current();
      });

    return () => {
      cancelled = true;
    };
  }, [cbInstance, paymentIntentId, priceTotal, onHydratedRef, onUnavailableRef]);
};
